import { useState, useEffect } from 'react';

interface JobTimerProps {
  jobId: string;
  onTimeUpdate: (seconds: number) => void;
  initialSeconds?: number;
}

export default function JobTimer({ jobId, onTimeUpdate, initialSeconds = 0 }: JobTimerProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    let interval: number | undefined;

    if (isRunning && !isPaused) {
      interval = window.setInterval(() => {
        setSeconds((prev) => {
          const newSeconds = prev + 1;
          onTimeUpdate(newSeconds);
          // Save to localStorage for persistence
          localStorage.setItem(`job_timer_${jobId}`, newSeconds.toString());
          return newSeconds;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, isPaused, jobId, onTimeUpdate]);

  // Load saved time on mount
  useEffect(() => {
    const savedTime = localStorage.getItem(`job_timer_${jobId}`);
    if (savedTime) {
      const savedSeconds = parseInt(savedTime, 10);
      setSeconds(savedSeconds);
      onTimeUpdate(savedSeconds);
    }
  }, [jobId, onTimeUpdate]);

  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    setIsRunning(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleResume = () => {
    setIsPaused(false);
  };

  const handleStop = () => {
    setIsRunning(false);
    setIsPaused(false);
    localStorage.setItem(`job_timer_${jobId}`, seconds.toString());
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the timer? This cannot be undone.')) {
      setSeconds(0);
      setIsRunning(false);
      setIsPaused(false);
      onTimeUpdate(0);
      localStorage.removeItem(`job_timer_${jobId}`);
    }
  };

  return (
    <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Job Timer</h3>
        <div className="flex items-center gap-2">
          <i className="ri-time-line text-xl text-[#ff751f]"></i>
          {isRunning && !isPaused && (
            <span className="flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
          )}
        </div>
      </div>

      <div className="text-center mb-6">
        <div className="text-5xl font-bold text-gray-900 font-mono tracking-wider mb-2">
          {formatTime(seconds)}
        </div>
        <p className="text-sm text-gray-500">
          {isRunning && !isPaused && 'Timer Running'}
          {isRunning && isPaused && 'Timer Paused'}
          {!isRunning && seconds > 0 && 'Timer Stopped'}
          {!isRunning && seconds === 0 && 'Ready to Start'}
        </p>
      </div>

      <div className="flex gap-2">
        {!isRunning && (
          <button
            onClick={handleStart}
            className="flex-1 bg-[#ff751f] text-white px-4 py-3 rounded-lg font-semibold hover:bg-[#e66a1c] transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-play-fill text-xl"></i>
            Start Timer
          </button>
        )}

        {isRunning && !isPaused && (
          <button
            onClick={handlePause}
            className="flex-1 bg-yellow-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-pause-fill text-xl"></i>
            Pause
          </button>
        )}

        {isRunning && isPaused && (
          <button
            onClick={handleResume}
            className="flex-1 bg-green-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-play-fill text-xl"></i>
            Resume
          </button>
        )}

        {isRunning && (
          <button
            onClick={handleStop}
            className="flex-1 bg-red-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-stop-fill text-xl"></i>
            Stop
          </button>
        )}

        {seconds > 0 && (
          <button
            onClick={handleReset}
            className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-restart-line text-xl"></i>
            Reset
          </button>
        )}
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-800">
          <i className="ri-information-line mr-1"></i>
          Timer automatically saves your progress. Labour cost will be calculated based on total time.
        </p>
      </div>
    </div>
  );
}